import styled from '@emotion/styled';
import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';

import { usePrevious } from 'app/components/v2/hooks/usePrevious';

import { ResizeObserverDimensions, useResizeObserver } from '../v2/hooks';

// Local Typings
interface Props {
  disableOnExpandedHeightChange?: boolean;
  isExpanded: boolean;
  transitionSpeedInMs?: number;
}
interface WrapperProps extends Props {
  height: number | string;
  transitionSpeedInMs: number;
}

// Local Variables
const Wrapper = styled.div<WrapperProps>(props => ({
  height: props.height,
  overflow: 'hidden',
  position: 'relative',
  transition: `${props.transitionSpeedInMs}ms`,
  visibility: props.isExpanded ? 'visible' : 'collapse',
}));
function getHeight(
  didMount: boolean,
  isExpanded: boolean,
  innerHeight: number,
): number | string {
  if (didMount) {
    return isExpanded ? innerHeight : 0;
  }

  return isExpanded ? 'auto' : innerHeight;
}

// Component Definition
const VerticalExpander: FC<PropsWithChildren<Props>> = ({
  disableOnExpandedHeightChange,
  transitionSpeedInMs,
  ...props
}) => {
  const innerDivRef = useRef<HTMLDivElement>(null);
  const [didMount, setDidMount] = useState(false);
  const [shouldTransition, setShouldTransition] = useState(true);
  const [innerHeight, setInnerHeight] = useState(0);
  const defaultSpeed = innerHeight * 5;

  const handleResize = ({ height }: ResizeObserverDimensions) => {
    setInnerHeight(height);
  };

  useResizeObserver(innerDivRef,
    handleResize);

  useEffect(() => {
    if (innerDivRef?.current) {
      setInnerHeight(innerDivRef.current.clientHeight);
    }
  },
  [innerDivRef, props.children]);

  useEffect(() => {
    setDidMount(true);
  },
  []);

  const currentHeight = getHeight(
    didMount,
    props.isExpanded,
    innerHeight,
  );

  const previousHeight = usePrevious(currentHeight);

  const hasPreviousHeight = !!previousHeight && previousHeight !== 'auto';
  const hasCurrentHeight = !!currentHeight && currentHeight !== 'auto';
  const wasPreviouslyOpen = hasPreviousHeight && hasCurrentHeight;

  const localShouldTransition = !disableOnExpandedHeightChange || !wasPreviouslyOpen;

  const speedInMs = transitionSpeedInMs ?? defaultSpeed;

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShouldTransition(localShouldTransition);
    },
    shouldTransition ? 0 : speedInMs);

    return () => {
      clearTimeout(timeout);
    };
  },
  [disableOnExpandedHeightChange, localShouldTransition, shouldTransition, speedInMs]);

  return (
    <Wrapper
      height={currentHeight}
      isExpanded={props.isExpanded}
      transitionSpeedInMs={shouldTransition ? speedInMs : 0}
    >
      <div ref={innerDivRef}>
        {props.children}
      </div>
    </Wrapper>
  );
};

export default VerticalExpander;
